import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useEventStream, SyncProgressEvent } from '@/hooks/useEventStream';
import { ScheduleNotification } from '@/services/notificationService';

interface EventStreamContextValue {
  // Connection
  isConnected: boolean;
  // Notifications
  unreadCount: number;
  latestNotification: ScheduleNotification | null;
  decrementUnreadCount: (count?: number) => void;
  resetUnreadCount: () => void;
  // Sync
  syncingLeagues: Set<string>;
  // Subscription methods for callbacks
  subscribeToSync: (callbacks: SyncCallbacks) => () => void;
  subscribeToNotifications: (callback: (notification: ScheduleNotification) => void) => () => void;
}

interface SyncCallbacks {
  onSyncStart?: (leagueId: string, jobId: string, event: SyncProgressEvent) => void;
  onSyncProgress?: (leagueId: string, message: string) => void;
  onSyncComplete?: (leagueId: string, result: SyncProgressEvent['result'], event: SyncProgressEvent) => void;
  onSyncError?: (leagueId: string, error: string, event: SyncProgressEvent) => void;
}

const EventStreamContext = createContext<EventStreamContextValue | null>(null);

export const EventStreamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Store subscribers
  const syncSubscribersRef = React.useRef<Set<SyncCallbacks>>(new Set());
  const notificationSubscribersRef = React.useRef<Set<(n: ScheduleNotification) => void>>(new Set());

  const {
    isConnected,
    unreadCount,
    latestNotification,
    decrementUnreadCount,
    resetUnreadCount,
    syncingLeagues,
  } = useEventStream({
    onNotification: (notification) => {
      notificationSubscribersRef.current.forEach(cb => cb(notification));
    },
    onSyncStart: (leagueId, jobId, event) => {
      syncSubscribersRef.current.forEach(cb => cb.onSyncStart?.(leagueId, jobId, event));
    },
    onSyncProgress: (leagueId, message) => {
      syncSubscribersRef.current.forEach(cb => cb.onSyncProgress?.(leagueId, message));
    },
    onSyncComplete: (leagueId, result, event) => {
      syncSubscribersRef.current.forEach(cb => cb.onSyncComplete?.(leagueId, result, event));
    },
    onSyncError: (leagueId, error, event) => {
      syncSubscribersRef.current.forEach(cb => cb.onSyncError?.(leagueId, error, event));
    },
  });

  // Push notification clicks are handled via URL params (notificationId) in SchedulePage.
  // The service worker appends notificationId to the navigation URL, and SchedulePage
  // calls markAsRead + decrementUnreadCount on mount. This avoids race conditions
  // where postMessage + navigate causes the message to be lost on page reload.

  const subscribeToSync = useCallback((callbacks: SyncCallbacks) => {
    syncSubscribersRef.current.add(callbacks);
    return () => {
      syncSubscribersRef.current.delete(callbacks);
    };
  }, []);

  const subscribeToNotifications = useCallback((callback: (notification: ScheduleNotification) => void) => {
    notificationSubscribersRef.current.add(callback);
    return () => {
      notificationSubscribersRef.current.delete(callback);
    };
  }, []);

  const value = useMemo(() => ({
    isConnected,
    unreadCount,
    latestNotification,
    decrementUnreadCount,
    resetUnreadCount,
    syncingLeagues,
    subscribeToSync,
    subscribeToNotifications,
  }), [
    isConnected,
    unreadCount,
    latestNotification,
    decrementUnreadCount,
    resetUnreadCount,
    syncingLeagues,
    subscribeToSync,
    subscribeToNotifications,
  ]);

  return (
    <EventStreamContext.Provider value={value}>
      {children}
    </EventStreamContext.Provider>
  );
};

// Hook to access notification-related state.
// Returns safe no-op defaults when used outside EventStreamProvider (e.g. admin portal).
const noop = () => {};
export const useNotifications = () => {
  const context = useContext(EventStreamContext);
  if (!context) {
    return {
      isConnected: false,
      unreadCount: 0,
      latestNotification: null,
      decrementUnreadCount: noop,
      resetUnreadCount: noop,
      subscribe: noop as any,
    };
  }
  return {
    isConnected: context.isConnected,
    unreadCount: context.unreadCount,
    latestNotification: context.latestNotification,
    decrementUnreadCount: context.decrementUnreadCount,
    resetUnreadCount: context.resetUnreadCount,
    subscribe: context.subscribeToNotifications,
  };
};

// Hook to access sync-related state
export const useSyncStream = (callbacks?: SyncCallbacks) => {
  const context = useContext(EventStreamContext);
  if (!context) {
    throw new Error('useSyncStream must be used within EventStreamProvider');
  }

  // Subscribe to callbacks if provided
  React.useEffect(() => {
    if (callbacks) {
      return context.subscribeToSync(callbacks);
    }
  }, [context, callbacks]);

  return {
    isConnected: context.isConnected,
    syncingLeagues: context.syncingLeagues,
  };
};
